/**
 * What may be uploaded where a raw ECU flash read is expected.
 *
 * Two gates, cheapest first: the file name's extension, then the file's actual
 * content (magic bytes + printable-text ratio). The content check is the real
 * rule — it catches a renamed file too — but the extension list earns its place
 * for the rest: an Autotuner or Magicmotorsport slave, a WinOLS project, a
 * DAMOS, a disc image or an uncompressed tar all open with bytes that look like
 * any other binary. It also answers before the file is read, so an obvious
 * mistake is rejected immediately and a large archive is never pulled into
 * memory to be told no.
 */

const BLOCKED_EXTENSIONS = {
  // Tuning-tool project and slave files — a tool's own working file, not a flash read.
  ols: "WinOLS project",
  olsx: "WinOLS project",
  slave: "Autotuner slave",
  mmf: "Magicmotorsport slave",
  a2l: "A2L description",
  damos: "DAMOS description",

  // Archives — the binary inside has to be extracted first.
  rar: "RAR archive",
  zip: "ZIP archive",
  "7z": "7-Zip archive",
  tar: "TAR archive",
  gz: "GZIP archive",
  bz2: "BZIP2 archive",

  // Documents.
  pdf: "PDF",
  txt: "text file",
  csv: "CSV",
  xls: "Excel workbook",
  xlsx: "Excel workbook",
  doc: "Word document",
  docx: "Word document",
  rtf: "RTF document",

  // Images.
  jpg: "JPEG image",
  jpeg: "JPEG image",
  png: "PNG image",
  bmp: "BMP image",
  gif: "GIF image",
  webp: "WebP image",
  tif: "TIFF image",
  tiff: "TIFF image",

  // Executables and disc images.
  exe: "Windows executable",
  dll: "Windows library",
  msi: "Windows installer",
  sys: "Windows driver",
  iso: "disc image",
};

/** The extension after the last dot, lowercased; empty when the name carries none. */
const extensionOf = (fileName) => {
  const name = fileName || "";
  const dot = name.lastIndexOf(".");
  // A leading dot is a hidden file, not an extension, and a trailing one is nothing.
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).trim().toLowerCase();
};

/**
 * Why this file cannot be uploaded, or null when its extension is no objection.
 *
 * An unknown or absent extension passes: plenty of genuine flash reads are named
 * `.bin`, `.ori`, `.mod` or nothing at all, so this only ever turns away what it
 * positively recognises.
 */
export const blockedFileTypeRejection = (fileName) => {
  const label = BLOCKED_EXTENSIONS[extensionOf(fileName)];
  if (!label) return null;
  return `${label} files can't be uploaded here. Upload the raw ECU flash read (.bin and similar).`;
};

const TEXT_SAMPLE = 8192;
const TEXT_RATIO_THRESHOLD = 0.9;

const hasAt = (b, offset, ...sig) => {
  if (b.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (b[offset + i] !== sig[i]) return false;
  }
  return true;
};

const starts = (b, ...sig) => hasAt(b, 0, ...sig);

// Containers, documents, media and databases recognised by their magic bytes.
const recognisedNonBinaryFormat = (b) => {
  if (starts(b, 0x50, 0x4b, 0x03, 0x04) || starts(b, 0x50, 0x4b, 0x05, 0x06) || starts(b, 0x50, 0x4b, 0x07, 0x08)) return "ZIP/Office/archive";
  if (starts(b, 0x52, 0x61, 0x72, 0x21, 0x1a, 0x07)) return "RAR archive";
  if (starts(b, 0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c)) return "7-Zip archive";
  if (starts(b, 0x1f, 0x8b)) return "GZIP archive";
  if (starts(b, 0x42, 0x5a, 0x68)) return "BZIP2 archive";
  if (starts(b, 0xfd, 0x37, 0x7a, 0x58, 0x5a, 0x00)) return "XZ archive";
  if (starts(b, 0x25, 0x50, 0x44, 0x46)) return "PDF";
  if (starts(b, 0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1)) return "Office document (doc/xls/ppt)";
  if (starts(b, 0x4d, 0x5a)) return "Windows executable";
  if (starts(b, 0x7f, 0x45, 0x4c, 0x46)) return "ELF executable";
  if (starts(b, 0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74, 0x20, 0x33, 0x00)) return "SQLite database";
  if (starts(b, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "PNG image";
  if (starts(b, 0xff, 0xd8, 0xff)) return "JPEG image";
  if (starts(b, 0x47, 0x49, 0x46, 0x38)) return "GIF image";
  if (starts(b, 0x42, 0x4d)) return "BMP image";
  if (starts(b, 0x49, 0x49, 0x2a, 0x00) || starts(b, 0x4d, 0x4d, 0x00, 0x2a)) return "TIFF image";
  if (starts(b, 0x00, 0x00, 0x01, 0x00)) return "icon (.ico)";
  if (starts(b, 0x52, 0x49, 0x46, 0x46)) return "RIFF media (wav/avi/webp)";
  if (starts(b, 0x49, 0x44, 0x33)) return "MP3 audio";
  if (starts(b, 0x4f, 0x67, 0x67, 0x53)) return "OGG media";
  if (starts(b, 0x66, 0x4c, 0x61, 0x43)) return "FLAC audio";
  if (starts(b, 0x1a, 0x45, 0xdf, 0xa3)) return "Matroska/WebM video";
  if (hasAt(b, 4, 0x66, 0x74, 0x79, 0x70)) return "MP4/MOV video";
  return null;
};

// A flash read is dense binary; XML, JSON, HTML and plain text are not.
const looksLikeText = (b) => {
  const n = Math.min(b.length, TEXT_SAMPLE);
  if (n === 0) return false;
  let printable = 0;
  for (let i = 0; i < n; i++) {
    const c = b[i];
    if (c === 0x09 || c === 0x0a || c === 0x0d || (c >= 0x20 && c <= 0x7e)) printable++;
  }
  return printable / n >= TEXT_RATIO_THRESHOLD;
};

/**
 * Why this file is not a raw ECU binary, or null when it is a candidate the
 * identification engine should decide on. Mirrors the server-side validation, so
 * a file that cannot possibly work is answered here instead of after an upload.
 */
export const ecuBinaryRejection = async (file) => {
  // Cheapest gate first — no read, and a clearer answer than "this looks like a
  // text file" for something the name already gives away.
  const byName = blockedFileTypeRejection(file?.name);
  if (byName) return byName;

  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes.length < 16) {
    return "This file is empty or too small to be an ECU flash read. Upload the raw binary.";
  }
  const format = recognisedNonBinaryFormat(bytes);
  if (format) {
    return `This is a ${format} file, not a raw ECU binary. Upload the raw flash read.`;
  }
  if (looksLikeText(bytes)) {
    return "This looks like a text file, not a raw ECU binary. Upload the raw flash read.";
  }
  return null;
};
